import type React from "react";
import { formatLocationAddressLine } from "@/lib/formatLocationAddress";
import type { ILocation } from "@/services/locationApi";

interface LocationCardProps {
  location: ILocation;
  isSelected?: boolean;
  compact?: boolean;
  onSelect?: () => void;
  containerRef?: React.Ref<HTMLDivElement>;
}

type LocationCtaAction =
  | "order_online_clicks"
  | "join_waitlist_button_clicks"
  | "catering_clicks";

function postLocationCtaClick(
  action: LocationCtaAction,
  location: ILocation,
  destinationUrl: string,
): void {
  window.parent.postMessage({ action, location: location.fieldData, destinationUrl }, "*");
}

function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.75 19.79 19.79 0 0 1 1.62 5.18 2 2 0 0 1 3.59 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 18l.19-1.08z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0 mt-1"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export const LocationCard: React.FC<LocationCardProps> = ({
  location,
  isSelected = false,
  compact = false,
  onSelect,
  containerRef,
}) => {
  const { fieldData } = location;

  return (
    <div
      ref={containerRef}
      onClick={onSelect}
      className={`flex flex-col gap-4 p-5 transition-colors ${
        compact ? "" : "border-b border-[#dbe1e5]"
      } ${
        isSelected
          ? "bg-blue-50 border-l-4 border-l-primary"
          : compact
            ? "bg-white"
            : "bg-white cursor-pointer hover:bg-gray-50"
      }`}
    >
      {/* Info */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2 pr-8">
          <h2 className="font-[Righteous-Regular] text-[20px] leading-[1.2] uppercase text-[#0b1c24]">
            {fieldData.name}
          </h2>
          {fieldData.isComingSoon ? (
            <span
              className={`inline-flex shrink-0 items-center rounded-full bg-[#ffcd20] px-3 py-1 font-[HvDTrial] font-normal leading-[1.5] text-[#0b1c24] antialiased ${compact ? "text-[12px]" : "text-[14px]"}`}
            >
              Coming Soon!
            </span>
          ) : null}
        </div>

        <p className="font-textregular text-[16px] leading-[1.5] text-[#0b1c24]">
          {formatLocationAddressLine(fieldData)}
        </p>

        {fieldData.phone && (
          <div className="flex gap-3 items-center pt-1">
            <PhoneIcon />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.parent.postMessage(`tel:${fieldData.phone}`, "*");
              }}
              className="text-[18px] leading-[1.3] font-bold text-[#0b1c24] hover:text-primary cursor-pointer font-textregular"
            >
              {fieldData.phone}
            </button>
          </div>
        )}

        {fieldData.openHours && (
          <div className="flex gap-3 items-start">
            <ClockIcon />
            <div className="text-[18px] leading-[1.35] text-[#0b1c24]">
              {fieldData.openHours
                ?.replace("<p>", "")
                .replace("</p>", "")
                .split("|")
                .map((part, i) => {
                  const [label, ...rest] = part.split(":");
                  const hasLabel = rest.length > 0;
                  const value = hasLabel ? rest.join(":").trim() : part.trim();
                  return (
                    <div key={i} className="flex gap-1">
                      {hasLabel ? (
                        <>
                          <span className="font-bold font-textregular">
                            {label.trim()}:
                          </span>
                          <span className="font-textregular">{value}</span>
                        </>
                      ) : (
                        <span className="font-textregular">{value}</span>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {fieldData.addressLink && (
          <div className="flex gap-3 items-center">
            <GlobeIcon />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.parent.postMessage(
                  {
                    action: "redirect-location",
                    body: `/location/${fieldData.slug}`,
                  },
                  "*",
                );
              }}
              className="text-[18px] font-textregular leading-[1.3] font-bold uppercase tracking-[0.5px] text-[#0b1c24] hover:text-primary cursor-pointer"
            >
              Visit website
            </button>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          {fieldData.orderLink && (
            <a
              href={fieldData.orderLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                const destinationUrl = fieldData.orderLink;
                if (!destinationUrl) return;
                postLocationCtaClick(
                  "order_online_clicks",
                  location,
                  destinationUrl,
                );
              }}
              className={`flex-1 flex items-center justify-center h-12 md:h-[41px] font-textregular  px-6 rounded-full bg-primary text-white font-bold uppercase text-center transition-colors hover:bg-secondary ${compact ? "text-[11px]" : "text-[14px]"}`}
            >
              Order Now
            </a>
          )}
          {fieldData.waitlistLink && (
            <a
              href={fieldData.waitlistLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                const destinationUrl = fieldData.waitlistLink;
                if (!destinationUrl) return;
                postLocationCtaClick(
                  "join_waitlist_button_clicks",
                  location,
                  destinationUrl,
                );
              }}
              className={`flex-1 flex items-center justify-center h-12 md:h-[41px] font-textregular  px-6 rounded-full border-2 border-primary text-primary font-bold uppercase text-center transition-colors hover:bg-primary hover:text-white ${compact ? "text-[11px]" : "text-[14px]"}`}
            >
              Join Waitlist
            </a>
          )}
        </div>

        {fieldData.cateringLink && (
          <a
            href={fieldData.cateringLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              const destinationUrl = fieldData.cateringLink;
              if (!destinationUrl) return;
              postLocationCtaClick("catering_clicks", location, destinationUrl);
            }}
            className={`w-full flex items-center justify-center h-12 md:h-[41px] px-6 rounded-full border-2 border-primary text-primary font-bold uppercase text-center transition-colors hover:bg-primary hover:text-white ${compact ? "text-[11px]" : "text-[14px]"}`}
          >
            Order Catering
          </a>
        )}
      </div>
    </div>
  );
};
